import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useHiddenAction — A reusable hook for triggering hidden functionality via repeated key presses.
 * 
 * Press a specific key N times within a time window to unlock hidden features.
 * 
 * Usage:
 *   const { isUnlocked, reset } = useHiddenAction({ key: 'x', presses: 15 });
 * 
 *   // Conditionally render hidden UI when unlocked
 *   {isUnlocked && <button>Secret Action</button>}
 * 
 *   // Call reset() when leaving context (e.g. closing a modal)
 * 
 * @param {Object} options
 * @param {string}   options.key          - The key to listen for, case-insensitive (default: 'x')
 * @param {number}   options.presses      - Number of presses required to unlock (default: 15)
 * @param {number}   options.timeout      - Max time window in ms for all presses (default: 10000)
 * @param {boolean}  options.active       - Whether the listener is active (default: true)
 * @param {boolean}  options.requireShift - Only count presses made while Shift is held (default: false)
 * @param {Function} options.onUnlock     - Optional callback fired when unlocked
 * @returns {{ isUnlocked: boolean, reset: Function }}
 */
const useHiddenAction = ({ key = 'x', presses = 15, timeout = 10000, active = true, requireShift = false, onUnlock } = {}) => {
    const [isUnlocked, setIsUnlocked] = useState(false);
    const unlockedRef = useRef(false);
    const pressCountRef = useRef(0);
    const timerRef = useRef(null);

    const reset = useCallback(() => {
        pressCountRef.current = 0;
        unlockedRef.current = false;
        setIsUnlocked(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!active) return;

        const handleKeyDown = (e) => {
            // Only count if the pressed key matches (case-insensitive)
            if (e.key.toLowerCase() !== key.toLowerCase()) return;
            if (requireShift && !e.shiftKey) return;
            // Holding the key down auto-repeats keydown — count real presses only
            if (e.repeat) return;

            // Don't count if user is typing in an input/textarea
            const tag = e.target.tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;

            if (unlockedRef.current) return;

            pressCountRef.current += 1;

            // Start or restart the timeout window
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                pressCountRef.current = 0;
            }, timeout);

            if (pressCountRef.current >= presses) {
                unlockedRef.current = true;
                setIsUnlocked(true);
                clearTimeout(timerRef.current);
                timerRef.current = null;
                if (onUnlock) onUnlock();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [key, presses, timeout, active, requireShift, onUnlock]);

    return { isUnlocked, reset };
};

export default useHiddenAction;
